<div class="detail-page container py-4" *ngIf="!loading && template">
  <button type="button" class="back-link mb-3 d-inline-flex align-items-center gap-1" (click)="goBack()">
    <mat-icon>arrow_back</mat-icon> Back to catalogue
  </button>

  <div class="row g-4">
    <div class="col-lg-6">
      <div class="meta-card">
        <div class="detail-header">
          <div class="detail-heading">
            <h1 class="detail-title mb-1">{{ template.title }}</h1>
            <div class="game-label">{{ template.game || 'Unknown game' }}</div>
          </div>
          <span class="version-chip" title="Template version">v{{ template.version }}</span>
        </div>
        <div
          class="revision-bar"
          *ngIf="template.updated_at || template.created_at"
        >
          <mat-icon>history</mat-icon>
          <span>Last updated</span>
          <time [attr.datetime]="template.updated_at || template.created_at">
            {{ (template.updated_at || template.created_at) | date: 'd MMM y, HH:mm' }}
          </time>
        </div>
        <div
          *ngIf="(isAuthor || isBotOwner) && template.status !== 'approved'"
          class="status-banner mb-3"
          [class.status-pending]="template.status === 'pending'"
          [class.status-denied]="template.status === 'denied'"
          [class.status-unlisted]="template.status === 'unlisted'"
        >
          <ng-container *ngIf="template.status === 'pending'">
            Pending review — hidden from the public catalogue.
          </ng-container>
          <ng-container *ngIf="template.status === 'denied'">
            Denied — you can submit a revision for review.
          </ng-container>
          <ng-container *ngIf="template.status === 'unlisted' && template.unlisted_by_admin">
            Unlisted by an admin — hidden from the public catalogue.
            <span *ngIf="!isBotOwner"> You cannot list it again; only an admin can restore it.</span>
            <span *ngIf="isBotOwner"> You can list it again or delete it.</span>
          </ng-container>
          <ng-container *ngIf="template.status === 'unlisted' && !template.unlisted_by_admin">
            Unlisted — hidden from the public catalogue. You can list it again or delete it.
          </ng-container>
        </div>
        <p class="detail-description">{{ template.description }}</p>
        <p *ngIf="showReviewerNotes" class="detail-notes">
          <strong>Reviewer notes:</strong> {{ template.notes }}
        </p>

        <div *ngIf="limitEntries.length" class="limits-block mb-3">
          <h2 class="limits-title">Sign-up limits</h2>
          <ul class="limits-list">
            <li *ngFor="let limit of limitEntries" [class.limit-child]="limit.depth > 0">
              <span class="limit-label" [innerHTML]="limit.label"></span>
              <strong>{{ limit.value }}</strong>
            </li>
          </ul>
        </div>

        <div class="vote-row mb-3" *ngIf="template.status === 'approved'">
          <button
            type="button"
            class="btn vote-btn"
            [class.active]="template.user_vote === 'like'"
            (click)="vote('like')"
          >
            <mat-icon>thumb_up</mat-icon> {{ template.like_count }}
          </button>
          <button
            type="button"
            class="btn vote-btn"
            [class.active]="template.user_vote === 'dislike'"
            (click)="vote('dislike')"
          >
            <mat-icon>thumb_down</mat-icon> {{ template.dislike_count }}
          </button>
        </div>

        <div class="action-row">
          <button
            *ngIf="isLoggedIn && template.status === 'approved'"
            type="button"
            class="btn btn-primary"
            (click)="openImport()"
          >
            Import to server
          </button>
          <button
            *ngIf="canEditLive"
            type="button"
            class="btn btn-outline-warning"
            (click)="openEdit()"
          >
            Edit listing
          </button>
          <button
            *ngIf="canResubmit"
            type="button"
            class="btn btn-outline-primary"
            (click)="openResubmit()"
          >
            Edit &amp; resubmit
          </button>
          <button
            *ngIf="canUnlist"
            type="button"
            class="btn btn-outline-secondary"
            [disabled]="actionBusy"
            (click)="unlist()"
          >
            Unlist
          </button>
          <button
            *ngIf="canRelist"
            type="button"
            class="btn btn-outline-success"
            [disabled]="actionBusy || relistBlockedByAdmin"
            [attr.title]="
              relistBlockedByAdmin
                ? 'This template was unlisted by an admin and cannot be listed again by you'
                : null
            "
            (click)="relist()"
          >
            List again
          </button>
          <button
            *ngIf="canDelete"
            type="button"
            class="btn btn-outline-danger"
            [disabled]="actionBusy"
            (click)="deleteListing()"
          >
            Delete
          </button>
          <button *ngIf="!isLoggedIn" type="button" class="btn btn-outline-primary" (click)="login()">
            Login to import or vote
          </button>
        </div>
      </div>
    </div>

    <div class="col-lg-6">
      <div class="preview-header mb-2">
        <h2 class="h5 mb-0">Event preview</h2>
      </div>
      <div class="preview-container">
        <app-embed-preview></app-embed-preview>
      </div>
    </div>
  </div>
</div>

<div *ngIf="loading" class="text-center py-5">
  <mat-spinner diameter="40" class="mx-auto"></mat-spinner>
</div>

<ng-template #importDialog>
  <h2 mat-dialog-title>Import template</h2>
  <mat-dialog-content>
    <p class="mb-3" *ngIf="guilds.length">Choose a server.</p>
    <mat-form-field appearance="outline" class="w-100" *ngIf="guilds.length">
      <mat-label>Server</mat-label>
      <mat-select [(ngModel)]="selectedGuildId">
        <mat-option *ngFor="let guild of guilds" [value]="guild.id">{{ guild.name }}</mat-option>
      </mat-select>
    </mat-form-field>
    <p *ngIf="!guilds.length" class="text-warning mb-0">
      No servers available. Invite the bot to a server where you can manage templates.
    </p>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Cancel</button>
    <button
      mat-flat-button
      color="primary"
      [disabled]="!selectedGuildId || importing || !guilds.length"
      (click)="confirmImport()"
    >
      Import
    </button>
  </mat-dialog-actions>
</ng-template>
